import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, Bell, ChevronsUpDown, LogOut, User, Settings, SlidersHorizontal, Check, Plus } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function TopBar({ onOpenMobile, org }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  const doLogout = async () => {
    track("logout");
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/80 glass px-4 sm:px-6" data-testid="app-topbar">
      <button
        onClick={onOpenMobile}
        data-testid="sidebar-mobile-trigger"
        aria-label="Open navigation"
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Organization selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="org-selector-btn"
            className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium transition-colors hover:border-foreground/30"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md brand-gradient-bg text-[11px] font-bold text-white">
              {(org?.name || "W").charAt(0)}
            </span>
            <span className="hidden max-w-[160px] truncate sm:inline">{org?.name || "Workspace"}</span>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
          <DropdownMenuItem className="gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md brand-gradient-bg text-[11px] font-bold text-white">
              {(org?.name || "W").charAt(0)}
            </span>
            <span className="flex-1 truncate">{org?.name || "Workspace"}</span>
            <Check className="h-4 w-4 text-brand-teal" />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled className="gap-2 text-muted-foreground">
            <Plus className="h-4 w-4" /> Create workspace <span className="ml-auto text-[10px]">Soon</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Global search (placeholder) */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogTrigger asChild>
          <button
            data-testid="global-search-btn"
            onClick={() => track("global_search_open")}
            className="ml-1 hidden flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-foreground/30 sm:flex md:max-w-md"
          >
            <Search className="h-4 w-4" />
            <span>Search workspace…</span>
            <kbd className="ml-auto rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium">⌘K</kbd>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Global search</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input autoFocus placeholder="Search across your workspace…" className="w-full bg-transparent text-sm outline-none" data-testid="global-search-input" />
          </div>
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Universal search arrives with the first Workspace modules. Soon you'll find customers,
            tasks, docs and more from right here.
          </p>
        </DialogContent>
      </Dialog>

      <div className="ml-auto flex items-center gap-2">
        {/* Notifications (placeholder) */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="notifications-btn"
              aria-label="Notifications"
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
            >
              <Bell className="h-[18px] w-[18px]" />
              <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-brand-teal" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80">
            <p className="font-display text-sm font-semibold">Notifications</p>
            <div className="mt-3 flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border py-8 text-center">
              <p className="text-sm font-medium text-muted-foreground">You're all caught up</p>
              <p className="text-xs text-muted-foreground/70">Activity and alerts will appear here.</p>
            </div>
          </PopoverContent>
        </Popover>

        <ThemeToggle />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="inline-flex items-center gap-2 rounded-full border border-border bg-card p-1 pr-2 transition-colors hover:border-foreground/30">
              <Avatar className="h-7 w-7">
                {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
                <AvatarFallback className="brand-gradient-bg text-xs font-semibold text-white">{initial}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium">{user?.name || "Member"}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild><Link to="/app/settings/profile" data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> Profile</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/app/settings/preferences" data-testid="menu-preferences"><SlidersHorizontal className="mr-2 h-4 w-4" /> Preferences</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/app/settings" data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" /> Settings</Link></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
